import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { CauseDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import type { CauseRegisterResponseRow } from '@risksmart-app/drizzle/src/dto';
import type {
  Cause_Bool_Exp,
  GetCausesQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetCausesDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import { CAUSE_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
} from '@/hooks/queries/dashboardFilterArgs';
import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

const useCausesGraphqlVariables = (args: InternalArgs) => {
  const { user } = useRisksmartUser();
  const entityWhere = useEntityWhereFilter<Cause_Bool_Exp>(
    Parent_Type_Enum.Cause
  );

  const { where } = buildDashboardWhere({
    base: entityWhere,
    filters: args,
    userId: user?.userId,
    datePredicates: CAUSE_DATE_PREDICATES,
    ownershipWhere: (conditions) => ({
      issue: conditions.issueFilterConditions,
    }),
  });

  return { where };
};

type InternalArgs = UseGetCauseRegisterArgs & {
  entityIds?: string[];
};

/**
 * Cause's `dashboardFilterConfig` declares no tag or department filter, and
 * `cause.register` takes only the date half of the shared input fragment
 * (COI-697), so the arg type takes the same subset.
 */
export type UseGetCauseRegisterArgs = Pick<
  DashboardFilterArgs<CauseDashboardDateField>,
  'dateRange' | 'ownership'
>;

/**
 * Maps TRPC cause data to match the GraphQL query structure
 */
export const mapTrpcCausesToGraphQL = (
  trpcData: CauseRegisterResponseRow[]
): StrictMappedGraphQLSelection<GetCausesQuery> => {
  return {
    cause: trpcData,
  };
};

const useGetCauseRegisterBase = createQueryHook<
  InternalArgs,
  CauseRegisterResponseRow[],
  GetCausesQuery
>({
  trpcQueryOptions: (trpc, { entityIds, dateRange, ownership }) =>
    trpc.frontend.cause.register.queryOptions({
      entityIds,
      dateRange,
      ownership,
    }),
  mapTrpcDataToGraphQL: mapTrpcCausesToGraphQL,
  graphqlDocument: GetCausesDocument,
  graphqlVariables: useCausesGraphqlVariables,
});

/**
 * Fetches causes with automatic entity filtering for both GraphQL and tRPC
 * paths. Entity filtering uses useEntityFilter context internally — consumers
 * don't need to pass entityIds. Dashboard filters (COI-691) are all optional.
 */
export const useGetCauseRegister = (params: {
  queryArgs: UseGetCauseRegisterArgs;
  shouldSkip?: boolean;
  suppressErrorNotification?: boolean;
}) => {
  const { entityIds } = useEntityFilter();

  return useGetCauseRegisterBase({
    queryArgs: {
      ...params.queryArgs,
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
    suppressErrorNotification: params.suppressErrorNotification,
  });
};
